import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  EMPLOYEE_CLOCK_TENANT_KEY,
  EMPLOYEE_CLOCK_TOKEN_KEY,
  EMPLOYEE_CLOCK_USER_KEY,
  EMPLOYEE_CLOCK_USERNAME_KEY,
} from "./employeeClockStorage";

const API_URL = process.env.REACT_APP_API_URL1;
const DEFAULT_BRAND = { tenant_name: "PeopleHub", logo_url: "/logo.png", primary_color: "#1976d2" };

export default function EmployeeClockLogin() {
  const navigate = useNavigate();
  const [tenant, setTenant] = useState(localStorage.getItem(EMPLOYEE_CLOCK_TENANT_KEY) || localStorage.getItem("tenant") || "");
  const [email, setEmail] = useState(localStorage.getItem(EMPLOYEE_CLOCK_USERNAME_KEY) || "");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [brand, setBrand] = useState(DEFAULT_BRAND);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const tenantCode = tenant.trim();
    if (!tenantCode) {
      setBrand(DEFAULT_BRAND);
      return;
    }

    const timer = window.setTimeout(async () => {
      try {
        const res = await fetch(`${API_URL}/system/tenant-profile`, {
          headers: { "X-Tenant": tenantCode.toUpperCase() },
        });
        const data = await res.json();
        if (data?.success && data?.data) {
          setBrand({
            tenant_name: data.data.tenant_name || "PeopleHub",
            logo_url: data.data.logo_url || "/logo.png",
            primary_color: data.data.primary_color || "#1976d2",
          });
        }
      } catch (_) {
        setBrand(DEFAULT_BRAND);
      }
    }, 300);

    return () => window.clearTimeout(timer);
  }, [tenant]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/web-clock/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Tenant": tenant.trim(),
        },
        body: JSON.stringify({ email: email.trim(), password }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || "Login failed");
      }

      localStorage.setItem(EMPLOYEE_CLOCK_TENANT_KEY, tenant.trim().toUpperCase());
      localStorage.setItem(EMPLOYEE_CLOCK_TOKEN_KEY, data.token);
      localStorage.setItem(EMPLOYEE_CLOCK_USER_KEY, JSON.stringify(data.user));
      localStorage.setItem("tenant", tenant.trim().toUpperCase());
      if (remember) {
        localStorage.setItem(EMPLOYEE_CLOCK_USERNAME_KEY, email.trim());
      } else {
        localStorage.removeItem(EMPLOYEE_CLOCK_USERNAME_KEY);
      }

      navigate("/web-clock", { replace: true });
    } catch (err) {
      setError(err.message || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f8fafc", display: "flex", alignItems: "center", justifyContent: "center", p: 2 }}>
      <Paper sx={{ width: "100%", maxWidth: 420, borderRadius: 3, border: "1px solid #e2e8f0", p: 3 }}>
        <Stack spacing={2.5} component="form" onSubmit={handleSubmit}>
          <Box sx={{ textAlign: "center" }}>
            {brand.logo_url && (
              <Box
                component="img"
                src={brand.logo_url}
                alt={`${brand.tenant_name || "PeopleHub"} logo`}
                onError={() => setBrand((prev) => ({ ...prev, logo_url: "/logo.png" }))}
                sx={{ height: 54, maxWidth: 180, objectFit: "contain", mb: 1.5 }}
              />
            )}
            <Typography variant="h5" sx={{ fontWeight: 900, color: "#0f172a" }}>
              {brand.tenant_name || "PeopleHub"}
            </Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 800, color: brand.primary_color || "#1976d2", mt: 0.25 }}>
              PeopleHub Employee Clock
            </Typography>
            <Typography variant="body2" sx={{ color: "#64748b", mt: 0.75 }}>Use your mobile app credentials to clock in or out from a browser.</Typography>
          </Box>

          {error && <Alert severity="error">{error}</Alert>}

          <TextField
            label="Tenant Code"
            value={tenant}
            onChange={(e) => setTenant(e.target.value.toUpperCase())}
            autoComplete="organization"
            required
            fullWidth
          />
          <TextField
            label="Username or Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="username"
            required
            fullWidth
          />
          <TextField
            label="Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
            fullWidth
          />

          <FormControlLabel
            control={<Checkbox checked={remember} onChange={(e) => setRemember(e.target.checked)} />}
            label="Remember tenant and username"
          />

          <Button type="submit" variant="contained" size="large" disabled={loading || !tenant || !email || !password} sx={{ py: 1.3, fontWeight: 800, bgcolor: brand.primary_color || "#1976d2" }}>
            {loading ? "Signing in..." : "Sign In"}
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
}
